import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';

import { apiService, UploadTelemetryEvent } from '../services/api';

export interface PendingUploadFile {
  file: File;
  // Stable per-add id used for status updates in the modal.
  id: string;
  uploadId: string;
  fileFingerprint: string;
  attemptNum: number;
  // Notified each tick with a 0..1 progress fraction.
  onProgress?: (fraction: number) => void;
  // Notified once tusd assigns a server upload id.
  onSessionCreated?: (tusUploadId: string) => void;
  onResume?: () => void;
  onTelemetry?: (event: UploadTelemetryEvent) => void;
  // Notified on terminal upload outcome (background, post-modal-close).
  onDone?: (path: string) => void;
  onError?: (err: unknown) => void;
}

export interface StartCaseArgs {
  caseName?: string;
  ocr?: Record<string, unknown>;
  files: PendingUploadFile[];
}

export interface UploadsContextValue {
  /**
   * Begin uploading the given files and register a case job. Awaits
   * tus-session creation for each file (so the backend gets real
   * tus_upload_ids), then calls POST /cases. Resolves with the new
   * case/job ids; the actual byte uploads continue in the background
   * and survive modal unmount.
   */
  startCaseUpload: (
    args: StartCaseArgs,
  ) => Promise<{ jobId: string; caseId: string }>;
  /** Abort all uploads belonging to the given case job (best-effort). */
  abortCaseUploads: (jobId: string) => void;
}

const UploadsContext = createContext<UploadsContextValue | null>(null);

interface ActiveUpload {
  controller: AbortController;
  promise: Promise<unknown>;
}

export const UploadsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // jobId -> array of active uploads. Held in a ref so the registry
  // survives all renders (including modal unmount).
  const registryRef = useRef<Map<string, ActiveUpload[]>>(new Map());

  const startCaseUpload = useCallback(
    async ({ caseName, ocr, files }: StartCaseArgs) => {
      if (files.length === 0) {
        throw new Error('No files to upload');
      }

      // Each entry resolves once tusd assigns it an upload id (POST /tus/
      // returned 201). We need all ids before calling POST /cases.
      const sessionResolvers: { resolve: (id: string) => void; reject: (e: unknown) => void; promise: Promise<string> }[] = [];
      const sessionPromises = files.map(() => {
        let resolveFn!: (id: string) => void;
        let rejectFn!: (e: unknown) => void;
        const p = new Promise<string>((resolve, reject) => {
          resolveFn = resolve;
          rejectFn = reject;
        });
        sessionResolvers.push({ resolve: resolveFn, reject: rejectFn, promise: p });
        return p;
      });

      // Kick off all uploads. Each one's onSessionCreated resolves the
      // matching session promise. The byte upload continues in the
      // background; we hold a reference to the controller and promise
      // so we can abort later if the user cancels the case.
      const activeUploads: ActiveUpload[] = files.map((entry, idx) => {
        const controller = new AbortController();
        const promise = apiService
          .uploadPDFTus(entry.file, {
            signal: controller.signal,
            uploadId: entry.uploadId,
            fileFingerprint: entry.fileFingerprint,
            attemptNum: entry.attemptNum,
            onProgress: entry.onProgress,
            onResume: entry.onResume,
            onTelemetry: entry.onTelemetry,
            onSessionCreated: (tusUploadId: string) => {
              entry.onSessionCreated?.(tusUploadId);
              sessionResolvers[idx].resolve(tusUploadId);
            },
          })
          .then(result => {
            entry.onDone?.(result.path);
            return result;
          })
          .catch(err => {
            // Reject the session promise too so createCase below errors
            // out cleanly if a session never opened.
            sessionResolvers[idx].reject(err);
            entry.onError?.(err);
            throw err;
          });
        return { controller, promise };
      });

      let tusUploadIds: string[];
      try {
        tusUploadIds = await Promise.all(sessionPromises);
      } catch (err) {
        // Abort any uploads that did get going — we can't register a
        // partial case.
        activeUploads.forEach(u => u.controller.abort());
        throw err;
      }

      const caseFiles = files.map((f, idx) => ({
        filename: f.file.name,
        expected_size: f.file.size,
        tus_upload_id: tusUploadIds[idx],
      }));

      let caseResp: { job_id: string; case_id: string; status: string; status_endpoint: string };
      try {
        caseResp = await apiService.createCase(caseName, caseFiles, ocr);
      } catch (err) {
        activeUploads.forEach(u => u.controller.abort());
        throw err;
      }

      const newJobId = caseResp.job_id;
      registryRef.current.set(newJobId, activeUploads);
      // Drop the registry entry once all uploads settle.
      Promise.allSettled(activeUploads.map(u => u.promise)).finally(() => {
        registryRef.current.delete(newJobId);
      });

      return { jobId: newJobId, caseId: caseResp.case_id };
    },
    [],
  );

  const abortCaseUploads = useCallback((jobId: string) => {
    const list = registryRef.current.get(jobId);
    if (!list) return;
    list.forEach(u => u.controller.abort());
    registryRef.current.delete(jobId);
  }, []);

  const value = useMemo<UploadsContextValue>(
    () => ({ startCaseUpload, abortCaseUploads }),
    [startCaseUpload, abortCaseUploads],
  );

  return <UploadsContext.Provider value={value}>{children}</UploadsContext.Provider>;
};

export const useUploads = (): UploadsContextValue => {
  const ctx = useContext(UploadsContext);
  if (!ctx) {
    throw new Error('useUploads must be used within an UploadsProvider');
  }
  return ctx;
};
